import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Monitor, Volume2, Dumbbell } from 'lucide-react';
import { GlassCard } from '@/components/GlassCard';
import { AudioToggle } from '@/components/AudioToggle';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useApp } from '@/context/AppContext';
import { audioService } from '@/services/audio-service';

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="mb-3 flex items-center gap-2">
    <Icon size={18} className="text-[#38BDF8]" />
    <h2 className="text-lg font-bold text-white">{children}</h2>
  </div>
);

const Row = ({ label, description, children, testId }) => (
  <div
    className="flex items-center justify-between rounded-2xl bg-white/5 p-4"
    data-testid={testId}
  >
    <div>
      <p className="font-semibold text-white">{label}</p>
      {description && <p className="text-xs text-[#CBD5E1]">{description}</p>}
    </div>
    {children}
  </div>
);

const themeOptions = [
  { key: 'light', label: 'Light', icon: Sun },
  { key: 'dark', label: 'Dark', icon: Moon },
  { key: 'system', label: 'System', icon: Monitor },
];

export const Settings = () => {
  const { settings, setSettings, setAudio } = useApp();

  return (
    <div className="flex flex-col gap-7">
      <header>
        <p className="text-sm font-medium text-[#CBD5E1]">Personalize your experience</p>
        <h1 className="mt-1 text-3xl font-extrabold text-white sm:text-4xl">
          Settings
        </h1>
      </header>

      {/* Audio */}
      <section>
        <SectionTitle icon={Volume2}>Audio</SectionTitle>
        <GlassCard className="flex flex-col gap-3">
          <AudioToggle
            enabled={settings.audio.enabled}
            onChange={(v) => setAudio({ enabled: v })}
          />
          <Row label="Countdown voice" description="Spoken 3-2-1 (UI only)">
            <Switch
              checked={settings.audio.countdownVoice}
              onCheckedChange={(v) => setAudio({ countdownVoice: v })}
              aria-label="Countdown voice"
              data-testid="countdown-voice-switch"
            />
          </Row>
          <div className="rounded-2xl bg-white/5 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-semibold text-white">Volume</span>
              <span className="text-sm text-[#38BDF8] tabular-nums">
                {Math.round(settings.audio.volume * 100)}%
              </span>
            </div>
            <Slider
              value={[settings.audio.volume * 100]}
              min={0}
              max={100}
              step={5}
              onValueChange={([v]) => {
                setAudio({ volume: v / 100 });
                audioService.setVolume(v / 100);
              }}
              aria-label="Volume"
              data-testid="volume-slider"
            />
          </div>
        </GlassCard>
      </section>

      {/* Appearance */}
      <section>
        <SectionTitle icon={Sun}>Appearance</SectionTitle>
        <GlassCard>
          <div className="grid grid-cols-3 gap-3">
            {themeOptions.map(({ key, label, icon: Icon }) => {
              const active = settings.theme === key;
              return (
                <motion.button
                  key={key}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => setSettings({ theme: key })}
                  data-testid={`theme-${key}`}
                  className="flex flex-col items-center gap-2 rounded-2xl border p-4 transition-colors"
                  style={{
                    borderColor: active ? '#38BDF8' : 'rgba(255,255,255,0.1)',
                    background: active ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.03)',
                  }}
                >
                  <Icon size={22} color={active ? '#38BDF8' : '#CBD5E1'} />
                  <span
                    className="text-sm font-semibold"
                    style={{ color: active ? '#38BDF8' : '#CBD5E1' }}
                  >
                    {label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </GlassCard>
      </section>

      {/* Workout */}
      <section>
        <SectionTitle icon={Dumbbell}>Workout</SectionTitle>
        <GlassCard className="flex flex-col gap-3">
          <Row
            label="Keep screen awake"
            description="Prevents device from sleeping"
            testId="keep-awake-row"
          >
            <Switch
              checked={settings.keepAwake}
              onCheckedChange={(v) => setSettings({ keepAwake: v })}
              aria-label="Keep screen awake"
              data-testid="keep-awake-switch"
            />
          </Row>
          <Row
            label="Auto-start next interval"
            description="No tap between phases"
            testId="auto-start-row"
          >
            <Switch
              checked={settings.autoStart}
              onCheckedChange={(v) => setSettings({ autoStart: v })}
              aria-label="Auto start"
              data-testid="auto-start-switch"
            />
          </Row>
          <Row
            label="Countdown before start"
            description={`${settings.countdownSeconds}s get-ready phase`}
            testId="countdown-before-row"
          >
            <Switch
              checked={settings.countdownBefore}
              onCheckedChange={(v) => setSettings({ countdownBefore: v })}
              aria-label="Countdown before start"
              data-testid="countdown-before-switch"
            />
          </Row>
        </GlassCard>
      </section>
      <p className="pb-4 text-center text-xs text-[#64748B]">
        NeoSport · Tabata HIIT · 100% offline
      </p>
    </div>
  );
};

export default Settings;
